/**
 * BomTab — BOM-BTA1 config/template tab cho Production Report
 * (spec §7 — `Excel_Production_Report_Web_UI_Conversion_Spec.md`).
 *
 * Tables (BOM items + variable processing + handling) áp skill `/table-style`:
 * header gradient via `app-table-header-cell`, body striping via
 * `app-table-wrap`. Header/info card + KPI cards giữ modern style.
 *
 * Hiện read-only mock. Khi BE expose `bom_template` + versioning, switch
 * sang editable mode + audit log (xem §7.7).
 */
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import {
  BOM_HEADER,
  BOM_ITEM_ROWS,
  BOM_KPIS,
  FX_RATE,
  HANDLING_COSTS,
  POWER_CONSUMPTION,
  VARIABLE_PROCESSING_COSTS,
} from '../productionReportMock'
import { fmtPercentScaled, fmtPlain, fmtUsd, fmtUsdPrecise } from './formatters'
import {
  COLORS,
  kpiAccent,
  neutralChipSx,
  sectionCardSx,
  sectionHeaderSx,
  sectionTitleSx,
  type KpiTone,
} from './styleTokens'

/* ---------- BOM header card --------------------------------------------- */

function BomHeaderCard({ bomCode }: { bomCode?: string }) {
  const h = BOM_HEADER
  // BomCode load theo PO No đang chọn (PoList.bom_code). Fallback mock nếu
  // PO chưa khớp row (vd mock seed, hoặc PO Number gõ tay chưa có trong list).
  const displayBomCode = bomCode?.trim() || h.bomCode
  return (
    <Box sx={sectionCardSx}>
      <Box sx={sectionHeaderSx}>
        <Stack spacing={0.25}>
          <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
            BOM Template
          </Typography>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: COLORS.text }}>BOM-{displayBomCode}</Typography>
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          label="Read-only · Edit requires admin"
          sx={{
            height: 24,
            fontSize: '0.72rem',
            fontWeight: 600,
            bgcolor: COLORS.warnSoft,
            color: COLORS.warn,
            border: `1px solid ${COLORS.warn}`,
          }}
        />
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
          <HeaderMeta label="Input quality" value={h.inputQuality} />
          <HeaderMeta label="Target quality" value={h.targetQuality} />
          <HeaderMeta label="Year / Month" value={`${h.year} / ${String(h.month).padStart(2, '0')}`} />
          <HeaderMeta label="Last revised" value={h.lastRevised} />
          <HeaderMeta label="LIFFE" value={fmtUsd(h.liffePrice)} tone="primary" />
          <HeaderMeta label="G2 Diffs" value={fmtUsd(h.g2Diffs)} tone="primary" />
          <HeaderMeta label="HĐ mua tháng" value={h.purchaseMonth} />
        </Stack>
      </Box>
    </Box>
  )
}

function HeaderMeta({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: KpiTone }) {
  const color = tone === 'primary' ? COLORS.primary : COLORS.text
  return (
    <Stack spacing={0.25} sx={{ minWidth: 120 }}>
      <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color }}>{value}</Typography>
    </Stack>
  )
}

/* ---------- KPI cards --------------------------------------------------- */

function KpiCards() {
  const k = BOM_KPIS
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
      <KpiCard label="Total input" value={fmtPercentScaled(k.totalInput, 0)} tone="primary" subtitle="Always 100%" />
      <KpiCard label="Total value in" value={fmtUsd(k.totalValueIn)} subtitle="per Mt" />
      <KpiCard label="Total out-turn" value={fmtPercentScaled(k.totalOutturn, 1)} tone="success" />
      <KpiCard label="Total value out" value={fmtUsd(k.totalValueOut)} subtitle="per Mt" />
      <KpiCard label="Variable processing" value={fmtUsdPrecise(k.variableProcessingCosts)} subtitle="USD / Mt" tone="warn" />
      <KpiCard label="Total upgrading cost" value={fmtUsdPrecise(k.totalUpgradingCosts)} subtitle="USD / Mt" tone="warn" />
      <KpiCard label="Extracted value / Mt" value={fmtUsdPrecise(k.extractedValuePerMt)} tone="success" />
    </Stack>
  )
}

function KpiCard({ label, value, tone = 'neutral', subtitle }: { label: string; value: string; tone?: KpiTone; subtitle?: string }) {
  const stripe = kpiAccent(tone)
  const valueColor = tone === 'success' ? COLORS.success : tone === 'warn' ? COLORS.warn : tone === 'danger' ? COLORS.danger : COLORS.text
  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 150,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 1.5,
        bgcolor: COLORS.bgPage,
        px: 1.75,
        py: 1.25,
        borderLeft: `3px solid ${stripe}`,
      }}
    >
      <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: valueColor, lineHeight: 1.2, mt: 0.4 }}>{value}</Typography>
      {subtitle ? <Typography sx={{ fontSize: '0.72rem', color: COLORS.textSubtle, mt: 0.25 }}>{subtitle}</Typography> : null}
    </Box>
  )
}

/* ---------- Main BOM table ---------------------------------------------- */

function BomTable() {
  return (
    <Stack spacing={0.75}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={sectionTitleSx}>BOM item ratios</Typography>
        <Chip size="small" label={`${BOM_ITEM_ROWS.length} rows`} sx={neutralChipSx} />
      </Stack>
      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap">
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell className="app-table-header-cell" style={{ width: 110 }}>Item</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 130 }}>Category</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 90 }}>Premium</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 110 }}>Value</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 90 }}>Input %</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 110 }}>Value In</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 90 }}>Output %</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 110 }}>Value Out</TableCell>
                <TableCell align="center" className="app-table-header-cell" style={{ width: 80 }}>Active</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 120 }}>Contribution</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {BOM_ITEM_ROWS.map((r) => {
                const isLoss = r.item === 'Loss'
                return (
                  <TableRow key={r.item}>
                    <TableCell sx={{ fontWeight: 600 }}>{r.item}</TableCell>
                    <TableCell>{r.category}</TableCell>
                    <TableCell align="right">{fmtUsd(r.premium)}</TableCell>
                    <TableCell align="right">{fmtUsd(r.value)}</TableCell>
                    <TableCell align="right">{r.inputPct === 0 ? '' : fmtPercentScaled(r.inputPct, 1)}</TableCell>
                    <TableCell align="right">{r.valueIn === 0 ? '' : fmtUsd(r.valueIn)}</TableCell>
                    <TableCell align="right">{r.outputPct === 0 ? '' : fmtPercentScaled(r.outputPct, 1)}</TableCell>
                    <TableCell align="right">{r.valueOut === 0 ? '' : fmtUsdPrecise(r.valueOut)}</TableCell>
                    <TableCell align="center">
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          minWidth: 22,
                          px: 0.75,
                          py: 0.1,
                          borderRadius: 999,
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          bgcolor: r.active === 'Y' ? COLORS.successSoft : COLORS.bgHeader,
                          color: r.active === 'Y' ? COLORS.success : COLORS.textSubtle,
                        }}
                      >
                        {r.active}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: isLoss ? COLORS.danger : undefined }}>
                      {r.valueContribution === 0 ? '' : fmtUsdPrecise(r.valueContribution)}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Stack>
  )
}

/* ---------- Cost sections ---------------------------------------------- */

function CostsSection() {
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      <CostCard title="Variable processing costs" rows={VARIABLE_PROCESSING_COSTS} unit="USD / Mt" />
      <Box sx={{ ...sectionCardSx, flex: '0 0 260px' }}>
        <Box sx={sectionHeaderSx}>
          <Typography sx={sectionTitleSx}>FX rate</Typography>
          <Chip size="small" label="VND / USD" sx={neutralChipSx} />
        </Box>
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: COLORS.text }}>{fmtPlain(FX_RATE, 0)}</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: COLORS.textSubtle, mt: 0.5 }}>From `Update_Liffe`</Typography>
        </Box>
      </Box>
      <CostCard title="Handling cost" rows={HANDLING_COSTS} unit="USD / Mt" />
    </Stack>
  )
}

interface CostCardProps {
  title: string
  rows: { label: string; amount: number }[]
  unit: string
}

function CostCard({ title, rows, unit }: CostCardProps) {
  return (
    <Stack spacing={0.75} sx={{ flex: '1 1 340px', minWidth: 280 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={sectionTitleSx}>{title}</Typography>
        <Chip size="small" label={unit} sx={neutralChipSx} />
      </Stack>
      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className="app-table-header-cell">Item</TableCell>
                <TableCell align="right" className="app-table-header-cell">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const isTotal = r.label.toLowerCase() === 'total'
                return (
                  <TableRow key={r.label}>
                    <TableCell sx={{ fontWeight: isTotal ? 700 : 400, color: isTotal ? COLORS.text : COLORS.textMuted, bgcolor: isTotal ? COLORS.bgSubtle : 'inherit', borderTop: isTotal ? `1px solid ${COLORS.borderStrong}` : undefined }}>
                      {r.label}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: isTotal ? 700 : 600, bgcolor: isTotal ? COLORS.bgSubtle : 'inherit', borderTop: isTotal ? `1px solid ${COLORS.borderStrong}` : undefined }}>
                      {fmtUsdPrecise(r.amount)}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Stack>
  )
}

/* ---------- Power consumption ------------------------------------------ */

function PowerConsumptionCard() {
  const p = POWER_CONSUMPTION
  return (
    <Box sx={sectionCardSx}>
      <Box sx={sectionHeaderSx}>
        <Typography sx={sectionTitleSx}>Power consumption</Typography>
        <Chip size="small" label="kWh / VND" sx={neutralChipSx} />
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <KpiCard label="New meter (kWh)" value={fmtPlain(p.newMeter, 0)} />
          <KpiCard label="Old meter (kWh)" value={fmtPlain(p.oldMeter, 0)} />
          <KpiCard label="kWh consumed" value={fmtPlain(p.kwhConsumed, 0)} tone="primary" />
          <KpiCard label="Unit price (VND)" value={fmtPlain(p.unitPrice, 0)} />
          <KpiCard label="Power cost (VND)" value={fmtPlain(p.powerCost, 0)} tone="warn" />
          <KpiCard label="Cost / ton (VND)" value={fmtPlain(p.powerCostPerTon, 0)} tone="warn" />
        </Stack>
      </Box>
    </Box>
  )
}

/* ---------- main tab --------------------------------------------------- */

export interface BomTabProps {
  /** Bom code load từ PO List row khớp PO No đang chọn ở header.
   *  Empty → fallback BOM_HEADER mock. */
  bomCode?: string
}

export default function BomTab({ bomCode }: BomTabProps = {}) {
  return (
    <Stack spacing={2}>
      <BomHeaderCard bomCode={bomCode} />
      <KpiCards />
      <BomTable />
      <CostsSection />
      <PowerConsumptionCard />
    </Stack>
  )
}
